import { useState, useCallback, useMemo } from 'react';
import {
  Button,
  Tag,
  Space,
  Row,
  Col,
  Tooltip,
  message,
  Card,
  Typography,
  Radio,
  Badge,
  Spin,
  Table,
  Dropdown,
  Select,
  Input
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  TableOutlined,
  AppstoreOutlined,
  MoreOutlined
} from '@ant-design/icons';
import type { MaintenanceRequest, MaintenanceStatus, MaintenancePriority } from '@/types/maintenance';
import { getMaintenanceRequests, deleteMaintenanceRequest, updateMaintenanceRequest } from '@/api/maintenance';
import { useAuth } from '@/contexts/AuthContext';
import MaintenanceModal from '@/components/features/maintenance/MaintenanceModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import CommonCalendar from '@/components/common/Calendar';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

const { Title, Text } = Typography;
const { Button: RadioButton } = Radio;
const { Option } = Select;

const MAINTENANCE_STATUSES: { value: MaintenanceStatus; labelKey: string }[] = [
  { value: 'Open', labelKey: 'maintenance:status.open' },
  { value: 'In Progress', labelKey: 'maintenance:status.in_progress' },
  { value: 'On Hold', labelKey: 'maintenance:status.on_hold' },
  { value: 'Resolved', labelKey: 'maintenance:status.resolved' }
];

const MAINTENANCE_PRIORITIES: { value: MaintenancePriority; labelKey: string }[] = [
  { value: 'Low', labelKey: 'maintenance:priority.low' },
  { value: 'Medium', labelKey: 'maintenance:priority.medium' },
  { value: 'High', labelKey: 'maintenance:priority.high' },
  { value: 'Urgent', labelKey: 'maintenance:priority.urgent' }
];

type ViewMode = 'board' | 'calendar' | 'table';

const API_STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

export default function Maintenance() {
  const { t } = useTranslation(['maintenance', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  // State
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [viewMode, setViewMode] = useState<ViewMode>('board');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedMaintenanceRequest, setSelectedMaintenanceRequest] = useState<MaintenanceRequest | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedPriority, setSelectedPriority] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Queries
  const { data: maintenanceData = { maintenanceRequests: [], lastDoc: undefined, total: 0 }, isLoading } = useQuery({
    queryKey: ['maintenance', pagination.current, pagination.pageSize, searchTerm, selectedStatus, selectedPriority, firestoreUser?.workspace?.id],
    queryFn: async () => {
      if (!firestoreUser) {
        throw new Error('User not authenticated');
      }
      return getMaintenanceRequests(firestoreUser, {
        searchTerm,
        status: selectedStatus || undefined,
        priority: selectedPriority || undefined,
        pageSize: pagination.pageSize,
        page: pagination.current,
        firestoreUser
      });
    },
    enabled: !!firestoreUser,
    staleTime: API_STALE_TIME_MS,
  });

  const maintenanceList = maintenanceData.maintenanceRequests;

  // Mutations
  const deleteMaintenanceMutation = useMutation({
    mutationFn: (id: string) => deleteMaintenanceRequest(id, firestoreUser!),
    onSuccess: () => {
      message.success(t('maintenance:deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      setIsDeleteModalVisible(false);
      setSelectedMaintenanceRequest(null);
    },
    onError: (error: any) => {
      message.error(error.message || t('common:errorOccurred'));
    }
  });

  const updateMaintenanceMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<MaintenanceRequest> }) => 
      updateMaintenanceRequest(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('maintenance:updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
    },
    onError: (error: any) => {
      message.error(error.message || t('common:errorOccurred'));
    }
  });

  // Memoized values
  const maintenanceByStatus = useMemo(() => ({
    'Open': maintenanceList.filter(request => request.status === 'Open'),
    'In Progress': maintenanceList.filter(request => request.status === 'In Progress'),
    'On Hold': maintenanceList.filter(request => request.status === 'On Hold'),
    'Resolved': maintenanceList.filter(request => request.status === 'Resolved')
  }), [maintenanceList]);

  // Utility functions
  const getStatusColor = (status: MaintenanceStatus) => {
    switch (status) {
      case 'Resolved':
        return 'success';
      case 'In Progress':
        return 'processing';
      case 'On Hold':
        return 'warning';
      default:
        return 'blue';
    }
  };

  const getPriorityColor = (priority: MaintenancePriority) => {
    switch (priority) {
      case 'Urgent':
        return 'red';
      case 'High':
        return 'orange';
      case 'Medium':
        return 'blue';
      default:
        return 'green';
    }
  };

  const handleAddMaintenance = useCallback(() => {
    setSelectedMaintenanceRequest(null);
    setIsModalOpen(true);
  }, []);

  const handleEditMaintenance = useCallback((maintenanceRequest: MaintenanceRequest) => {
    setSelectedMaintenanceRequest(maintenanceRequest);
    setIsModalOpen(true);
  }, []);

  const handleViewMaintenance = useCallback((maintenanceRequest: MaintenanceRequest) => {
    navigate(`/maintenance/${maintenanceRequest.id}`, { 
      state: { 
        maintenanceRequest,
        from: 'list'
      } 
    });
  }, [navigate]);

  const handleDeleteMaintenance = useCallback((maintenanceRequest: MaintenanceRequest) => {
    setSelectedMaintenanceRequest(maintenanceRequest);
    setIsDeleteModalVisible(true);
  }, []);

  const handleStatusChange = useCallback((maintenanceRequestId: string, newStatus: MaintenanceStatus) => {
    updateMaintenanceMutation.mutate({ id: maintenanceRequestId, data: { status: newStatus } });
  }, [updateMaintenanceMutation]);

  const handlePriorityChange = useCallback((maintenanceRequestId: string, newPriority: MaintenancePriority) => {
    updateMaintenanceMutation.mutate({ id: maintenanceRequestId, data: { priority: newPriority } });
  }, [updateMaintenanceMutation]);

  const handleModalSuccess = useCallback(() => {
    setIsModalOpen(false);
    setSelectedMaintenanceRequest(null);
  }, []);

  const handleModalCancel = useCallback(() => {
    setIsModalOpen(false);
    setSelectedMaintenanceRequest(null);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (selectedMaintenanceRequest) {
      deleteMaintenanceMutation.mutate(selectedMaintenanceRequest.id);
    }
  }, [selectedMaintenanceRequest, deleteMaintenanceMutation]);

  const handleDeleteCancel = useCallback(() => {
    setIsDeleteModalVisible(false);
    setSelectedMaintenanceRequest(null);
  }, []);

  const renderMaintenanceCard = useCallback((maintenanceRequest: MaintenanceRequest) => {
    const dropdownItems = [
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: t('common:actions.edit'),
        onClick: (e: any) => {
          e.stopPropagation();
          handleEditMaintenance(maintenanceRequest);
        }
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: t('common:actions.delete'),
        danger: true,
        onClick: (e: any) => {
          e.stopPropagation();
          handleDeleteMaintenance(maintenanceRequest);
        }
      }
    ];

    return (
      <Card
        key={maintenanceRequest.id}
        size="small"
        style={{ 
          margin: '8px', 
          minHeight: '160px',
          cursor: 'pointer',
          position: 'relative'
        }}
        onClick={() => handleViewMaintenance(maintenanceRequest)}
        hoverable
      >
        {/* Dropdown menu in top right */}
        <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
          <Dropdown
            menu={{ items: dropdownItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        </div>

        <div>
          <div style={{ marginBottom: '8px', paddingRight: '32px' }}>
            <Text strong style={{ fontSize: '14px' }}>{maintenanceRequest.title}</Text>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <Space size="small">
              <Tag color={getPriorityColor(maintenanceRequest.priority)}>
                {t(MAINTENANCE_PRIORITIES.find(p => p.value === maintenanceRequest.priority)?.labelKey || '')}
              </Tag>
            </Space>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Reported by: {maintenanceRequest.reportedBy.name}
            </Text>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Block: {maintenanceRequest.block.name}
            </Text>
          </div>
          {maintenanceRequest.apartmentUnit && (
            <div style={{ marginBottom: '8px' }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Unit: {maintenanceRequest.apartmentUnit.name}
              </Text>
            </div>
          )}
          <div style={{ marginBottom: '12px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Created: {dayjs(maintenanceRequest.createdAt).format('MMM D, YYYY')}
            </Text>
          </div>
          
          {/* Editable Status and Priority */}
          <Row gutter={8} style={{ marginTop: '8px' }}>
            <Col span={12}>
              <Select
                size="small"
                value={maintenanceRequest.status}
                onChange={(value) => handleStatusChange(maintenanceRequest.id, value)}
                onClick={(e) => e.stopPropagation()}
                style={{ width: '100%' }}
                styles={{ popup: { root: { fontSize: '12px' } } }}
              >
                {MAINTENANCE_STATUSES.map(status => (
                  <Option key={status.value} value={status.value} style={{ fontSize: '12px' }}>
                    <Tag color={getStatusColor(status.value)} style={{ fontSize: '9px' }}>
                      {t(status.labelKey)}
                    </Tag>
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={12}>
              <Select
                size="small"
                value={maintenanceRequest.priority}
                onChange={(value) => handlePriorityChange(maintenanceRequest.id, value)}
                onClick={(e) => e.stopPropagation()}
                style={{ width: '100%' }}
                styles={{ popup: { root: { fontSize: '12px' } } }}
              >
                {MAINTENANCE_PRIORITIES.map(priority => (
                  <Option key={priority.value} value={priority.value} style={{ fontSize: '12px' }}>
                    <Tag color={getPriorityColor(priority.value)} style={{ fontSize: '11px' }}>
                      {t(priority.labelKey)}
                    </Tag>
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </div>
      </Card>
    );
  }, [handleViewMaintenance, handleEditMaintenance, handleDeleteMaintenance, handleStatusChange, handlePriorityChange, t]);

  const renderBoardView = useCallback(() => (
    <div style={{ height: 'calc(100vh - 200px)', minHeight: '600px' }}>
      <Row gutter={[16, 16]} style={{ height: '100%' }}>
        {MAINTENANCE_STATUSES.map(status => (
          <Col xs={24} sm={12} md={6} key={status.value} style={{ height: '100%' }}>
            <Card
              title={
                <Space>
                  <Tag color={getStatusColor(status.value)}>
                    {t(status.labelKey)}
                  </Tag>
                  <Badge count={maintenanceByStatus[status.value].length} />
                </Space>
              }
              size="small"
              style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              bodyStyle={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column',
                padding: '12px',
                overflow: 'hidden'
              }}
            >
              {maintenanceByStatus[status.value].length > 0 ? (
                <div style={{ 
                  flex: 1, 
                  overflowY: 'auto',
                  minHeight: 0
                }}>
                  {maintenanceByStatus[status.value].map(maintenanceRequest => renderMaintenanceCard(maintenanceRequest))}
                </div>
              ) : (
                <div style={{ 
                  flex: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  minHeight: '200px'
                }}>
                  <Text type="secondary" style={{ textAlign: 'center' }}>
                    No {t(status.labelKey).toLowerCase()} requests
                  </Text>
                </div>
              )}
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  ), [maintenanceByStatus, renderMaintenanceCard, t]);

  const renderCalendarView = useCallback(() => (
    <CommonCalendar
      events={maintenanceList}
      selectedDate={selectedDate}
      onSelectDate={setSelectedDate}
      eventTypeColors={{
        'Open': 'blue',
        'In Progress': 'processing',
        'On Hold': 'warning',
        'Resolved': 'success'
      }}
      getEventDate={(maintenanceRequest: MaintenanceRequest) => dayjs(maintenanceRequest.createdAt)}
      getEventTitle={(maintenanceRequest: MaintenanceRequest) => maintenanceRequest.title}
      getEventType={(maintenanceRequest: MaintenanceRequest) => maintenanceRequest.status}
      getEventColor={(maintenanceRequest: MaintenanceRequest) => {
        switch (maintenanceRequest.status) {
          case 'Open': return 'blue';
          case 'In Progress': return 'processing';
          case 'On Hold': return 'warning';
          case 'Resolved': return 'success';
          default: return 'default';
        }
      }}
      onEventClick={(maintenanceRequest: MaintenanceRequest) => handleViewMaintenance(maintenanceRequest)}
      showEventCount={false}
      defaultView="month"
    />
  ), [maintenanceList, selectedDate, handleViewMaintenance]);

  const renderTableView = useCallback(() => {
    const columns = [
      {
        title: t('maintenance:title'),
        dataIndex: 'title',
        key: 'title',
        render: (text: string, record: MaintenanceRequest) => (
          <Button type="link" onClick={() => handleViewMaintenance(record)}>
            {text}
          </Button>
        ),
      },
      {
        title: t('maintenance:statusLabel'),
        dataIndex: 'status',
        key: 'status',
        render: (status: MaintenanceStatus) => (
          <Tag color={getStatusColor(status)}>
            {t(MAINTENANCE_STATUSES.find(s => s.value === status)?.labelKey || '')}
          </Tag>
        ),
      },
      {
        title: t('maintenance:priorityLabel'),
        dataIndex: 'priority',
        key: 'priority',
        render: (priority: MaintenancePriority) => (
          <Tag color={getPriorityColor(priority)}>
            {t(MAINTENANCE_PRIORITIES.find(p => p.value === priority)?.labelKey || '')}
          </Tag>
        ),
      },
      {
        title: t('maintenance:reportedBy'),
        dataIndex: ['reportedBy', 'name'],
        key: 'reportedBy',
      },
      {
        title: t('maintenance:block'),
        dataIndex: ['block', 'name'],
        key: 'block',
      },
      {
        title: t('maintenance:apartmentUnit'),
        dataIndex: ['apartmentUnit', 'name'],
        key: 'apartmentUnit',
        render: (text: string) => text || '-',
      },
      {
        title: t('maintenance:assignedTo'),
        dataIndex: ['assignedTo', 'name'],
        key: 'assignedTo',
        render: (text: string) => text || '-',
      },
      {
        title: t('maintenance:createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (date: Date) => dayjs(date).format('MMM D, YYYY'),
        sorter: (a: MaintenanceRequest, b: MaintenanceRequest) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      },
      {
        title: t('common:actions.title'),
        key: 'actions',
        render: (record: MaintenanceRequest) => (
          <Space>
            <Tooltip title={t('common:actions.edit')}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditMaintenance(record)}
              />
            </Tooltip>
            <Tooltip title={t('common:actions.delete')}>
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteMaintenance(record)}
              />
            </Tooltip>
          </Space>
        ),
      },
    ];

    return (
      <Table
        dataSource={maintenanceList}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: maintenanceData.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} requests`,
          onChange: (page, pageSize) => {
            setPagination(prev => ({ 
              ...prev, 
              current: page, 
              pageSize: pageSize || prev.pageSize 
            }));
          }
        }}
        scroll={{ x: 1200 }}
      />
    );
  }, [maintenanceList, handleViewMaintenance, handleEditMaintenance, handleDeleteMaintenance, t]);

  // Loading state
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  const renderView = () => {
    switch (viewMode) {
      case 'board':
        return renderBoardView();
      case 'calendar':
        return renderCalendarView();
      case 'table':
        return renderTableView();
      default:
        return renderBoardView();
    }
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3}>{t('maintenance:title')}</Title>
        </Col>
        <Col>
          <Space>
            <Radio.Group 
              value={viewMode} 
              onChange={(e) => setViewMode(e.target.value)}
              buttonStyle="solid"
            >
              <RadioButton value="board">
                <AppstoreOutlined /> {t('maintenance:boardView')}
              </RadioButton>
              <RadioButton value="calendar">
                <CalendarOutlined /> {t('maintenance:calendarView')}
              </RadioButton>
              <RadioButton value="table">
                <TableOutlined /> {t('maintenance:tableView')}
              </RadioButton>
            </Radio.Group>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddMaintenance}
            >
              {t('maintenance:createRequest')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Search and Filters */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Input.Search
            placeholder={t('common:search.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            onSearch={() => setPagination(prev => ({ ...prev, current: 1 }))}
          />
        </Col>
        <Col xs={12} md={4}>
          <Select
            placeholder={t('maintenance:status')}
            value={selectedStatus}
            onChange={(value) => {
              setSelectedStatus(value);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            allowClear
            style={{ width: '100%' }}
          >
            {MAINTENANCE_STATUSES.map(status => (
              <Option key={status.value} value={status.value}>
                {t(status.labelKey)}
              </Option>
            ))}
          </Select>
        </Col>
        <Col xs={12} md={4}>
          <Select
            placeholder={t('maintenance:priority')}
            value={selectedPriority}
            onChange={(value) => {
              setSelectedPriority(value);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            allowClear
            style={{ width: '100%' }}
          >
            {MAINTENANCE_PRIORITIES.map(priority => (
              <Option key={priority.value} value={priority.value}>
                {t(priority.labelKey)}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      {renderView()}

      {/* Maintenance Modal */}
      <MaintenanceModal
        open={isModalOpen}
        maintenanceRequest={selectedMaintenanceRequest}
        onSuccess={handleModalSuccess}
        onCancel={handleModalCancel}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={isDeleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('maintenance:deleteConfirm')}
      />
    </div>
  );
} 